import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import moment from 'moment';

import InsiderSvc from '../../../../../services/dbServices/InsiderSvc';
import { UK_INSIGHTS, UK_INSIGHTS_TRIAL } from '../../../../../data/features';
import { setUKTopYearFilters, handleInvestorsFilter } from '../../../../../actions/ukPrivateCompany.actions';

import Preloader from '../../../../Preloader';
import Tooltip from '../../../../Tooltip';
import YearsFilter from '../../Filters/YearsFilter';
import TopInvestors from './TopInvestors';
import TopEquity from './TopEquity';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const TopSections = ({
  title, tooltipText, seeMoreUrl, type, extraParams = {}, hideFilters,
}) => {
  let mounted = true;
  const initialized = useRef(false);
  const [topSectionData, setTopSectionData] = useState({
    loading: true,
    newLoading: false,
    data: [],
  });
  const hasInsightsAccess = useSelector((state) => (
    (state.subscriptions.permissions.features || []).includes(UK_INSIGHTS)
  ));
  const hasInsightsTrialAccess = useSelector((state) => (
    (state.subscriptions.permissions.features || []).includes(UK_INSIGHTS_TRIAL)
  ));
  const investorsFilter = useSelector((state) => (
    state.ukPrivateCompany.investorsFilter
  ));
  const yearFilter = useSelector(({ ukPrivateCompany }) => ({
    fundraisingYear: ukPrivateCompany.fundraisingYear,
    investorsYear: ukPrivateCompany.investorsYear,
    marketYear: ukPrivateCompany.marketYear,
    roiYear: ukPrivateCompany.roiYear,
  }));
  const [selectedYear, setSelectedYear] = useState(yearFilter[`${type}Year`] || moment().year());
  const dispatch = useDispatch();
  const isInvestors = type === 'investors';
  const [sortField, setSortField] = useState(isInvestors ? investorsFilter.sortField : '');
  const [sortOrder, setSortOrder] = useState(isInvestors ? investorsFilter.sortOrder : '');

  const callAPI = async () => {
    let apiData = null;

    setTopSectionData({
      ...topSectionData,
      newLoading: !topSectionData.loading,
    });

    const params = {
      ...extraParams,
      per: 5,
    };

    if (!hideFilters) {
      params.year = selectedYear;
    }

    switch (type) {
      case 'fundraising': {
        apiData = await InsiderSvc.getCompanyInsiderFundraising(params);
        break;
      }
      case 'investors': {
        if (sortField) {
          params.order_by = sortField;
          if (sortOrder) {
            params.order = sortOrder.toUpperCase();
          }
        }
        apiData = await InsiderSvc.getCompanyInsiderInvestors(params);
        break;
      }
      case 'market': {
        apiData = await InsiderSvc.getCompanyInsiderMarket(params);
        break;
      }
      case 'roi': {
        apiData = await InsiderSvc.getCompanyInsiderRoi(params);
        break;
      }
      default:
        break;
    }

    if (!mounted) {
      return;
    }
    initialized.current = true;

    setTopSectionData({
      loading: false,
      newLoading: false,
      data: apiData || [],
    });
  };

  useEffect(() => {
    callAPI();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (isInvestors) {
      dispatch(handleInvestorsFilter({
        sortField,
        sortOrder,
      }));
    }
    if (!initialized.current) {
      return;
    }
    callAPI();
  }, [
    selectedYear, sortField, sortOrder,
  ]);

  const selectedYearChange = (value) => {
    setSelectedYear(value);
    dispatch(setUKTopYearFilters({
      [`${type}Year`]: value,
    }));
  };

  const onSort = (field) => {
    if (!hasInsightsAccess || hasInsightsTrialAccess) return;

    let newSortOrder = sortOrder;

    if (sortField === field) {
      newSortOrder = newSortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      newSortOrder = 'desc';
    }
    setSortOrder(newSortOrder);
    setSortField(field);
  };

  const hasChanges = false;
  const previousYear = moment(selectedYear, 'YYYY').subtract(1, 'year').year();

  return (
    <div className={cx('top-sections-holder', type, { has_changes: hasChanges })}>
      <div className={Styles['top-sections-holder-title']}>
        <div className={Styles['top-sections-holder-title-left']}>
          <div className={Styles['top-sections-holder-title-text']}>
            {title}
          </div>
          {tooltipText !== null && (
            <Tooltip
              newIcon
              className={cx('top-sections-holder-title-tooltip', type)}
              hasBigScreenDesign
              isCentered
              dynamicPosition
              contentStyle={{ left: 0 }}
            >
              {tooltipText}
            </Tooltip>
          )}
        </div>
        {!hideFilters && (
          <div className={Styles['top-sections-holder-title-right']}>
            <YearsFilter
              setStartEndYear={selectedYearChange}
              currentSelectedValue={selectedYear}
            />
          </div>
        )}
      </div>
      <div className={cx('top-sections')}>
        {topSectionData.loading ? (
          <Preloader
            centered
            loading
            static
            style={{
              margin: 'auto',
            }}
          />
        ) : (
          <>
            {topSectionData.newLoading && (
              <div className={Styles['top-sections-loading']}>
                <Preloader
                  centered
                  loading
                  static
                  style={{
                    margin: 'auto',
                  }}
                />
              </div>
            )}
            {!topSectionData.data.length ? (
              <div className={Styles['top-sections-no-data']}>
                <div className={Styles['top-sections-no-data-text']}>
                  No data currently available.
                </div>
              </div>
            ) : (
              <>
                {isInvestors ? (
                  <TopInvestors
                    type={type}
                    selectedYear={selectedYear}
                    hasChanges={hasChanges}
                    previousYear={previousYear}
                    hasInsightsAccess={hasInsightsAccess || hasInsightsTrialAccess}
                    topSectionData={topSectionData}
                    onSort={onSort}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    hasInsightsTrialAccess={hasInsightsTrialAccess}
                  />
                ) : (
                  <TopEquity
                    type={type}
                    selectedYear={selectedYear}
                    hasChanges={hasChanges}
                    previousYear={previousYear}
                    hasInsightsAccess={hasInsightsAccess || hasInsightsTrialAccess}
                    topSectionData={topSectionData}
                  />
                )}
                <div className={Styles['top-sections-see-more']}>
                  {topSectionData.data.length && (
                    <Link
                      to={seeMoreUrl}
                    >
                      See More
                      <FontAwesomeIcon icon={faChevronRight} />
                    </Link>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TopSections;
